/**
 * Detects when the AI is asking for specific information that would benefit from a text input field
 */

export interface InputDetectionResult {
  shouldShowInput: boolean
  placeholder: string
  confidence: number
}

/**
 * Keywords that indicate the AI is asking for specific information
 */
const INFORMATION_REQUEST_PATTERNS = [
  // Direct questions
  /\b(what|how|when|where|why|which|who)\b.*\?/i,
  /\b(tell me|describe|explain|share|give me)\b/i,
  /\b(what's|what is|what are|what do|what did|what would|what could)\b/i,
  
  // Specific information requests
  /\b(your name|your age|your location|your experience|your preference|your goal|your budget|your timeline)\b/i,
  /\b(how much|how many|how long|how often|how well)\b/i,
  /\b(what kind of|what type of|what sort of)\b/i,
  
  // Personal information
  /\b(about you|your situation|your needs|your requirements|your background)\b/i,
  /\b(your current|your previous|your past|your future)\b/i,
  
  // Specific details
  /\b(more details|more information|more context|more specifics)\b/i,
  /\b(elaborate|expand|clarify|specify)\b/i,
  
  // Open-ended prompts
  /\b(go ahead|feel free|please share|I'd love to hear|I'm curious about)\b/i,
  /\b(what do you think|what's your opinion|what's your experience)\b/i,
];

/**
 * Keywords that indicate the AI is NOT asking for specific information
 */
const NON_INPUT_PATTERNS = [
  // Greetings and pleasantries
  /\b(hello|hi|hey|good morning|good afternoon|good evening|welcome|thanks|thank you)\b/i,
  
  // Statements and affirmations
  /\b(that's great|that's wonderful|that's helpful|I understand|I see|got it|okay|alright)\b/i,
  
  // Instructions and explanations
  /\b(here's how|here's what|let me explain|I'll help you|I can help|I'm here to)\b/i,
  
  // Closing statements
  /\b(is there anything else|anything else|let me know|feel free to ask|happy to help)\b/i,
  
  // Product recommendations
  /\b(here are some|here's a|I recommend|I suggest|you might like|check out)\b/i,
];

/**
 * High-confidence patterns that strongly indicate input is needed
 */
const HIGH_CONFIDENCE_PATTERNS = [
  /\b(what is your|what are your|what's your|tell me about your)\b/i,
  /\b(how would you|how do you|how did you|how can you)\b/i,
  /\b(describe your|explain your|share your|give me your)\b/i,
  /\b(I'd like to know|I'm curious about|I want to understand)\b/i,
  /\b(please tell me|can you tell me|would you like to share)\b/i,
];

/**
 * Detects if the AI message is asking for specific information that would benefit from a text input
 */
export function detectInputNeed(aiMessage: string, followUpOptions: unknown[] = []): InputDetectionResult {
  const message = aiMessage.toLowerCase().trim()
  
  // If there are many follow-up options, it's likely a choice-based interaction
  if (followUpOptions.length > 2) {
    return {
      shouldShowInput: false,
      placeholder: '',
      confidence: 0
    }
  }
  
  // Check for high-confidence patterns first
  for (const pattern of HIGH_CONFIDENCE_PATTERNS) {
    if (pattern.test(message)) {
      return {
        shouldShowInput: true,
        placeholder: generatePlaceholder(message),
        confidence: 0.9
      }
    }
  }
  
  // Check for information request patterns
  let inputScore = 0
  for (const pattern of INFORMATION_REQUEST_PATTERNS) {
    if (pattern.test(message)) {
      inputScore += 1
    }
  }
  
  // Check for non-input patterns
  let nonInputScore = 0
  for (const pattern of NON_INPUT_PATTERNS) {
    if (pattern.test(message)) {
      nonInputScore += 1
    }
  }
  
  // Calculate confidence
  const confidence = Math.max(0, (inputScore - nonInputScore) / 3)
  
  return {
    shouldShowInput: confidence > 0.3,
    placeholder: generatePlaceholder(message),
    confidence
  }
}

/**
 * Generates an appropriate placeholder based on the message content
 */
function generatePlaceholder(message: string): string {
  const lowerMessage = message.toLowerCase()
  
  if (lowerMessage.includes('name')) {
    return "Type your name here..."
  }
  if (lowerMessage.includes('age') || lowerMessage.includes('old')) {
    return "Type your age here..."
  }
  if (lowerMessage.includes('location') || lowerMessage.includes('where')) {
    return "Type your location here..."
  }
  if (lowerMessage.includes('experience') || lowerMessage.includes('tried')) {
    return "Describe your experience..."
  }
  if (lowerMessage.includes('goal') || lowerMessage.includes('want to achieve')) {
    return "Describe your goal..."
  }
  if (lowerMessage.includes('budget') || lowerMessage.includes('spend')) {
    return "Type your budget here..."
  }
  if (lowerMessage.includes('preference') || lowerMessage.includes('prefer')) {
    return "Describe your preference..."
  }
  if (lowerMessage.includes('problem') || lowerMessage.includes('issue') || lowerMessage.includes('challenge')) {
    return "Describe your problem..."
  }
  if (lowerMessage.includes('question') || lowerMessage.includes('ask')) {
    return "Type your question here..."
  }
  
  // Default placeholder
  return "Type your answer here..."
}

/**
 * Enhanced detection that considers conversation context
 */
export function detectInputNeedWithContext(
  aiMessage: string, 
  followUpOptions: unknown[] = [],
  conversationHistory: unknown[] = [],
  category: string = 'general'
): InputDetectionResult {
  const basicResult = detectInputNeed(aiMessage, followUpOptions)
  
  // Adjust confidence based on conversation context
  let adjustedConfidence = basicResult.confidence
  
  // If this is early in the conversation, be more likely to show input
  if (conversationHistory.length < 3) {
    adjustedConfidence += 0.2
  }
  
  // If the AI just asked a question and there are few follow-up options, show input
  if (aiMessage.includes('?') && followUpOptions.length <= 2) {
    adjustedConfidence += 0.3
  }
  
  // Category-specific adjustments
  if (['Faith & Spirituality', 'Mental Health', 'Relationships', 'Parental Advice'].includes(category)) {
    // These categories often benefit from personal input
    adjustedConfidence += 0.1
  }
  
  return {
    shouldShowInput: adjustedConfidence > 0.3,
    placeholder: basicResult.placeholder,
    confidence: Math.min(1, adjustedConfidence)
  }
}
